'use client';

import React from 'react';
import TrailingLink from '@/components/atoms/TrailingLink';
import styles from './Footer.module.scss';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className={styles.footer} role="contentinfo">
      <div className={styles.container}>
        {/* Top Section - Company Info & Main Links */}
        <div className={styles.topSection}>
          <div className={styles.companyInfo}>
            <TrailingLink href="/" className={styles.logo}>
              <span className={styles.logoText}>BAYSGAiA Inc.</span>
            </TrailingLink>
            <p className={styles.tagline}>
              最先端技術と創造性で、新しい価値を創造する
            </p>
          </div>
        </div>

        {/* Middle Section - Navigation Links */}
        <div className={styles.middleSection}>
          <div className={styles.linksGrid}>
            {/* Company Section */}
            <div className={styles.linkGroup}>
              <h3 className={styles.linkGroupTitle}>会社情報</h3>
              <ul className={styles.linkList}>
                <li><TrailingLink href="/company/">会社概要</TrailingLink></li>
                <li><TrailingLink href="/company/philosophy/">企業理念</TrailingLink></li>
                <li><TrailingLink href="/company/team/">チーム紹介</TrailingLink></li>
                <li><TrailingLink href="/company/history/">会社沿革</TrailingLink></li>
              </ul>
            </div>

            {/* Services Section */}
            <div className={styles.linkGroup}>
              <h3 className={styles.linkGroupTitle}>サービス</h3>
              <ul className={styles.linkList}>
                <li><TrailingLink href="/services/system-development/">システム開発</TrailingLink></li>
                <li><TrailingLink href="/services/content-production/">コンテンツ制作</TrailingLink></li>
                <li><TrailingLink href="/services/consulting/">AIコンサルティング</TrailingLink></li>
              </ul>
            </div>

            {/* Resources Section */}
            <div className={styles.linkGroup}>
              <h3 className={styles.linkGroupTitle}>リソース</h3>
              <ul className={styles.linkList}>
                <li><TrailingLink href="/cases/">事例紹介</TrailingLink></li>
                <li><TrailingLink href="/news/">ニュース</TrailingLink></li>
                <li><TrailingLink href="/sustainability/">サステナビリティ</TrailingLink></li>
                <li><TrailingLink href="/careers/">採用情報</TrailingLink></li>
              </ul>
            </div>

            {/* Contact Section */}
            <div className={styles.linkGroup}>
              <h3 className={styles.linkGroupTitle}>お問い合わせ</h3>
              <ul className={styles.linkList}>
                <li><TrailingLink href="/contact/">お問い合わせフォーム</TrailingLink></li>
                <li><a href="tel:03-5948-4959">03-5948-4959</a></li>
                <li><a href="mailto:info@baysgaia.com">info@baysgaia.com</a></li>
              </ul>
            </div>
          </div>
        </div>

        {/* Company Details Section */}
        <div className={styles.detailsSection}>
          <div className={styles.companyDetails}>
            <h3 className={styles.visuallyHidden}>会社詳細情報</h3>
            <address className={styles.address}>
              <p>株式会社ベイスガイア</p>
              <p>〒170-0013 東京都豊島区東池袋1-34-5 いちご東池袋ビル6階</p>
              <p>営業時間: 平日 9:00 - 18:00（土日祝日休み）</p>
            </address>
          </div>

          {/* Social Links */}
          <div className={styles.socialLinks}>
            <h3 className={styles.visuallyHidden}>ソーシャルメディア</h3>
            <a href="https://twitter.com/baysgaia" target="_blank" rel="noopener noreferrer" aria-label="Twitter">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
            </a>
            <a href="https://www.linkedin.com/company/baysgaia" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19a.66.66 0 000 .14V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z"/>
              </svg>
            </a>
            <a href="https://github.com/baysgaia" target="_blank" rel="noopener noreferrer" aria-label="GitHub">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"/>
              </svg>
            </a>
          </div>
        </div>

        {/* Bottom Section - Legal Links & Copyright */}
        <div className={styles.bottomSection}>
          <nav className={styles.legalLinks} aria-label="Legal">
            <TrailingLink href="/privacy/">プライバシーポリシー</TrailingLink>
            <TrailingLink href="/terms/">利用規約</TrailingLink>
            <TrailingLink href="/sitemap/">サイトマップ</TrailingLink>
            <TrailingLink href="/accessibility/">アクセシビリティ方針</TrailingLink>
          </nav>
          <p className={styles.copyright}>
            © {currentYear} BAYSGAiA Inc. All rights reserved.<br />
            <small>
              Animation credits to pixiv Inc.’s VRoid Project
            </small>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;